import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGlobalSlice } from "app/slice";
import { MobileAccount } from "./mobile-account";
import { MobileAccountDialog } from "app/components/parts/mobile-account-dialog";
import {
  getMultiNetworkSelectionData,
  saveMultiNetworkSelectionData,
  saveAddNewLineCheckoutData,
  getAddNewLineCheckoutData,
} from "utils/purchase-flow-storage";

import { Label } from "app/components/ui/label";
import { Checkbox } from "app/components/ui/checkbox";
import { Input } from "app/components/ui/input";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";
import { toast } from "app/components/ui/use-toast";

interface Props {
  selectionOptions: string[];
}

interface OutletContext {
  isMobileAccountView: boolean;
  setIsMobileAccountView: React.Dispatch<React.SetStateAction<boolean>>;
}

type FormData = {
  selection: number;
  email: string;
  password: string;
};

export const SelectionSelector = ({ selectionOptions }: Props) => {
  const { isMobileAccountView, setIsMobileAccountView } =
    useOutletContext<OutletContext>();
  const { useVerifyCustomerMutation, actions } = useGlobalSlice();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showMobileAccountDialog, setShowMobileAccountDialog] = useState(false);

  const dispatch = useDispatch();
  const [
    verify,
    { isLoading, isSuccess, isError, error: verifyErrorMessage, data },
  ] = useVerifyCustomerMutation();

  const savedData = getMultiNetworkSelectionData();
  const form = useForm<FormData>({
    defaultValues: {
      selection: savedData?.selectionAndCoverage?.selection ?? undefined,
      email: savedData?.selectionAndCoverage?.email || "",
      password: savedData?.selectionAndCoverage?.password || "",
    },
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = form;

  const selectedIndex = watch("selection");

  const handleChange = (index: number) => {
    form.reset({
      selection: index,
      email: "",
      password: "",
    });
    setIsMobileAccountView(false);
    saveMultiNetworkSelectionData({
      selectionAndCoverage: {
        selection: index,
        email: "",
        password: "",
      },
    });
  };

  // Save to sessionStorage when form values change
  useEffect(() => {
    const subscription = watch((value) => {
      const selectionData = {
        selection: value.selection,
        email: value.email || "",
        password: value.password || "",
      };

      // Save to multi-network selection data (normal flow)
      saveMultiNetworkSelectionData({
        selectionAndCoverage: selectionData,
      });

      // Also save to add-new-line checkout data if user is in add-new-line context
      // This handles the case where user selects "I have an account" and then adds lines
      const addNewLineData = getAddNewLineCheckoutData();
      if (addNewLineData) {
        saveAddNewLineCheckoutData({
          ...addNewLineData,
          selectionAndCoverage: selectionData,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleSubmitVerifyCustomer = (data: FormData) => {
    if (selectedIndex === undefined || selectedIndex === null) {
      toast({
        title: "Selection Required",
        description: "Please select an option",
        variant: "error",
      });
      return;
    }

    if (selectedIndex !== 1) {
      navigate("/multi-network-phone-type");
      return;
    }

    if (data.email && data.password) {
      verify({
        email: data.email,
        password: data.password,
      });
    }
  };

  useEffect(() => {
    if (isSuccess && selectedIndex === 1 && data?.data) {
      // Validate network type before proceeding
      const userNetworkType =
        data?.data?.networkType || data?.data?.customer?.networkType;
      const activeLines = data?.data?.Activeline || 0;

      // Multi Network flow expects networkType = 2
      if (userNetworkType === 1) {
        toast({
          title: "Network Type Mismatch",
          description:
            "You are a Single Network user. This is for Multi Network users only. Please use the Single Network selection instead.",
          variant: "error",
        });
        return;
      }

      // If number of lines is 0 and network type is not 2, continue with network plan flow
      if (activeLines === 0 && userNetworkType !== 2) {
        dispatch(
          actions.setUser({
            user: {
              ...data?.data,
              ...data?.data?.customer,
              ...data?.data?.planData,
              _id: data?.data?.customerId,
            },
            rememberDevice: false,
          })
        );
        dispatch(actions.setLines({ numberOfLines: activeLines }));
        navigate("/multi-network-phone-type");
        return;
      }

      // Show mobile account dialog instead of setting isMobileAccountView
      // Mark that user was just verified to prevent auto-redirect
      sessionStorage.setItem("userJustVerified", "true");
      setShowMobileAccountDialog(true);
      dispatch(
        actions.setUser({
          user: {
            ...data?.data,
            ...data?.data?.customer,
            ...data?.data?.planData,
            _id: data?.data?.customerId,
          },
          rememberDevice: false,
        })
      );
      dispatch(actions.setLines({ numberOfLines: activeLines }));
    }
  }, [isSuccess, selectedIndex, data, dispatch, actions, navigate]);

  useEffect(() => {
    if (isError && verifyErrorMessage) {
      toast({
        title: "Verification Failed",
        description: verifyErrorMessage as string,
        variant: "error",
      });
    }
  }, [isError, verifyErrorMessage]);

  return (
    <FormProvider {...form}>
      <form
        className="w-full"
        onSubmit={handleSubmit(handleSubmitVerifyCustomer)}
      >
        <React.Fragment>
          <div className="bg-white p-4 pb-5 sm:p-5 sm:pb-6 lg:p-6 lg:pb-[2.125rem] border border-[#E5E7EB] rounded-xl md:rounded-2xl mb-4 sm:mb-5 lg:mb-6">
            <h4 className="mb-4 text-xl font-bold leading-5 text-black sm:text-[1.35rem] lg:text-2xl sm:leading-6 lg:leading-7 font-mobile sm:mb-6 lg:mb-9">
              Make a Selection
            </h4>
            <div className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5">
              {selectionOptions?.map((label, index) => {
                const id = `selection-${index}`;
                return (
                  <div
                    key={id}
                    className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5"
                  >
                    <div className="flex items-center gap-2 sm:gap-[0.625rem]">
                      <Checkbox
                        id={id}
                        checked={selectedIndex === index}
                        onCheckedChange={() => handleChange(index)}
                        className="w-5 h-5 md:w-[1.35rem] md:h-[1.35rem] lg:w-6 lg:h-6 rounded-[0.25rem] border-[#1D1D1B]"
                      />
                      <Label
                        htmlFor={id}
                        className="text-[#1D1D1B] text-sm md:text-[0.935rem] lg:text-base font-semibold leading-5 md:leading-6 mb-0 cursor-pointer"
                      >
                        {label}
                      </Label>
                    </div>
                  </div>
                );
              })}
              {selectedIndex === 1 && (
                <div className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5">
                  <div className="">
                    <Label
                      htmlFor="email"
                      className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
                    >
                      Enter your email address
                    </Label>
                    <div className="relative">
                      <Input
                        id="email"
                        type="email"
                        {...register("email", {
                          required: "Email is required!",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address",
                          },
                        })}
                        className="bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] border-[#D1D5DB] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline focus:outline-[#707C8F]"
                      />
                      {errors.email && (
                        <p className="text-[#D80027] text-[0.72rem] sm:text-xs font-normal leading-5 mt-0.5 sm:mt-1">
                          <span className="font-bold">*</span>
                          {errors.email?.message as string}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="">
                    <Label
                      htmlFor="password"
                      className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
                    >
                      Enter Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        {...register("password", {
                          required: "Password is required!",
                        })}
                        className="bg-white h-10 md:h-[2.875rem] p-2 pr-7 md:p-[0.78125rem] md:pr-9 md:pb-[0.84375rem] border-[#D1D5DB] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline focus:outline-[#707C8F]"
                        maxLength={14}
                      />
                      {watch("password") && (
                        <div className="absolute top-1/2 -translate-y-1/2 right-3 md:right-[0.875rem] w-4 md:w-[1.125rem] h-4 md:h-[1.125rem] cursor-pointer">
                          <img
                            src={
                              showPassword
                                ? "/assets/eye-close.svg"
                                : "/assets/eye-open.svg"
                            }
                            className="w-full h-auto"
                            onClick={() => setShowPassword(!showPassword)}
                            alt={showPassword ? "Hide" : "Show"}
                          />
                        </div>
                      )}
                    </div>
                    {errors.password && (
                      <p className="text-[#D80027] text-[0.72rem] sm:text-xs font-normal leading-5 mt-0.5 sm:mt-1">
                        <span className="font-bold">*</span>
                        {errors.password?.message as string}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {selectedIndex !== undefined && selectedIndex !== null && (
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? (
                <div className="flex items-center gap-0.5 sm:gap-1">
                  <Icons.Spinner className="animate-spin size-4 md:size-5" />
                  <span>Verifying...</span>
                </div>
              ) : (
                "Continue"
              )}
            </Button>
          )}
        </React.Fragment>
      </form>

      {/* Mobile Account Dialog */}
      <MobileAccountDialog
        isOpen={showMobileAccountDialog}
        onClose={() => {
          sessionStorage.removeItem("userJustVerified");
          setShowMobileAccountDialog(false);
        }}
        networkType={2}
        activeLines={data?.data?.Activeline}
        verifyData={data}
        verifyLoading={isLoading}
      />
    </FormProvider>
  );
};
